"use client";

import { useEffect, useRef } from "react";
import gsap from "gsap";
import { HeroBody } from "./HeroBody";
import { useReducedMotion } from "./useReducedMotion";
import type { HeroContent } from "./types";

export function FluxHero({ content }: { content: HeroContent }) {
  const rootRef = useRef<HTMLElement | null>(null);
  const bandA = useRef<HTMLDivElement | null>(null);
  const bandB = useRef<HTMLDivElement | null>(null);
  const bandC = useRef<HTMLDivElement | null>(null);
  const badgeRef = useRef<HTMLSpanElement | null>(null);
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  const subtitleRef = useRef<HTMLParagraphElement | null>(null);
  const ctasRef = useRef<HTMLDivElement | null>(null);
  const reduced = useReducedMotion();

  useEffect(() => {
    if (!rootRef.current) return;
    const ctx = gsap.context(() => {
      const intro = [badgeRef.current, titleRef.current, subtitleRef.current, ctasRef.current].filter(Boolean);
      if (reduced) {
        gsap.set(intro, { opacity: 1, y: 0 });
        return;
      }
      gsap.set(intro, { opacity: 0, y: 28 });
      gsap.to(intro, {
        opacity: 1,
        y: 0,
        duration: 0.9,
        ease: "power3.out",
        stagger: 0.1,
        delay: 0.1,
      });

      const bands = [bandA, bandB, bandC];
      bands.forEach((r, i) => {
        if (!r.current) return;
        gsap.to(r.current, {
          xPercent: i % 2 === 0 ? 12 : -10,
          skewX: i === 1 ? 2 : -1,
          duration: 7 + i * 1.2,
          ease: "sine.inOut",
          repeat: -1,
          yoyo: true,
        });
      });
    }, rootRef);
    return () => ctx.revert();
  }, [reduced]);

  const bandBase =
    "absolute left-[-20%] w-[140%] rounded-full blur-3xl will-change-transform";

  return (
    <section
      ref={rootRef}
      className="hero-section hero-grain relative isolate overflow-hidden bg-gradient-to-br from-[var(--primary-darker)] via-[#041018] to-[var(--primary-darker)] px-4 py-20 md:py-28"
      aria-label="Hero"
    >
      <div aria-hidden className="absolute inset-0 overflow-hidden">
        <div
          ref={bandA}
          className={bandBase}
          style={{
            top: "8%",
            height: "22%",
            background:
              "linear-gradient(90deg, transparent, color-mix(in oklch, var(--accent) 55%, transparent), transparent)",
            opacity: 0.55,
          }}
        />
        <div
          ref={bandB}
          className={bandBase}
          style={{
            top: "38%",
            height: "18%",
            background:
              "linear-gradient(90deg, transparent, color-mix(in oklch, var(--primary-light) 50%, transparent), transparent)",
            opacity: 0.45,
          }}
        />
        <div
          ref={bandC}
          className={bandBase}
          style={{
            top: "62%",
            height: "26%",
            background:
              "linear-gradient(90deg, transparent, color-mix(in oklch, var(--accent) 40%, #38bdf8), transparent)",
            opacity: 0.4,
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.12]"
          style={{
            backgroundImage:
              "repeating-linear-gradient(90deg, rgba(255,255,255,0.04) 0px, rgba(255,255,255,0.04) 1px, transparent 1px, transparent 72px)",
            maskImage:
              "radial-gradient(ellipse 85% 65% at 50% 40%, black 20%, transparent 75%)",
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 75% 55% at 50% 45%, color-mix(in oklch, var(--accent) 25%, transparent) 0%, transparent 70%), linear-gradient(to bottom, transparent 50%, rgba(0,0,0,0.55) 100%)",
          }}
        />
      </div>

      <HeroBody
        content={content}
        primaryVariant="primary"
        secondaryVariant="outline"
        refs={{ badge: badgeRef, title: titleRef, subtitle: subtitleRef, ctas: ctasRef }}
      />
    </section>
  );
}
